import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Card, Chip, CircularProgress, Grid, Icon, InputLabel, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tooltip,
} from "@mui/material";
import moment from "moment";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip as ChartTooltip, Legend } from "chart.js";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect";
import MDPeriodo from "components/MDPeriodo";
import { useMaterialUIController } from "context";
import { get } from "utils/WebService";
import { coresDoTema, TEXTO_COLORIDO_ESCURO } from "assets/coresTema";
import KpiCard from "views/coordenador/relatorios/analiseTempo/KpiCard";
import Painel from "views/coordenador/relatorios/analiseTempo/Painel";
import { formatarNumero, temaGrafico } from "views/coordenador/relatorios/analiseTempo/analiseUtils";
import ControlesIa from "./ControlesIa";
import { FUNCOES, SITUACOES, corDaFuncao, dinheiro, nomeCurto, tokens, trechoDaChave, usd, brl } from "./iaUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

// Cores dos cards (não são das funções: neutro, bom e ruim, sempre com ícone e texto)
const NEUTRO = { light: "#344767", dark: "#d9d9d9" };
const BOM = { light: "#13996b", dark: "#62cfa6" };
const RUIM = { light: "#d03b3b", dark: "#f08c8c" };
const ALERTA = { light: "#c98500", dark: "#f0b44a" };
// Texto de alerta (diferença na conferência): o âmbar das barras não passa como texto no claro
const TEXTO_ALERTA = { light: "#9a6700", dark: TEXTO_COLORIDO_ESCURO.warning };
const FONTE = "Roboto, Helvetica, Arial, sans-serif";
const rotuloFiltro = { px: 1, pb: 0.5, pt: 0.2 };
const OPCOES_FUNCAO = FUNCOES.map((f) => ({ value: f.codigo, label: f.curto }));
const cortar = (t, n) => (t && t.length > n ? `${t.substring(0, n - 1)}…` : t);
function rotuloQueCabe(valor) {
  return cortar(this.getLabelForValue(valor), Math.max(10, Math.floor(this.chart.width / 3 / 7)));
}

const Titulo = ({ icone, texto }) => (
  <MDBox component="span" display="inline-flex" alignItems="center" gap={0.75} sx={{ color: "inherit" }}>
    <Icon sx={{ fontSize: "1.1rem !important", opacity: 0.8, color: "inherit" }}>{icone}</Icon>
    {texto}
  </MDBox>
);

const Vazio = ({ texto }) => (
  <MDTypography component="div" variant="caption" color="text" display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4} sx={{ opacity: 0.75 }}>
    <Icon sx={{ fontSize: "2rem !important", mb: 0.5, color: "inherit" }}>psychology</Icon>
    {texto}
  </MDTypography>
);

const chipSituacao = (s) => {
  const x = SITUACOES[s] || { label: s || "-", cor: "default" };
  return (
    <Tooltip title={x.dica || ""}>
      <Chip size="small" variant="outlined" color={x.cor} label={x.label} sx={{ height: 20, fontSize: "0.7rem" }} />
    </Tooltip>
  );
};

/** Texto no fim da barra empilhada (opções: textos[i], cor). */
const textoNoFim = {
  id: "textoNoFimIa",
  afterDatasetsDraw(chart, args, opcoes) {
    if (!opcoes || !opcoes.textos) return;
    const { ctx } = chart;
    ctx.save();
    ctx.font = `600 11px ${FONTE}`;
    ctx.fillStyle = opcoes.cor;
    ctx.textBaseline = "middle";
    opcoes.textos.forEach((texto, i) => {
      let fimX = null;
      let y = null;
      chart.data.datasets.forEach((ds, di) => {
        const meta = chart.getDatasetMeta(di);
        if (meta.hidden || !meta.data[i]) return;
        const p = meta.data[i].getProps(["x", "y"], true);
        if (fimX === null || p.x > fimX) fimX = p.x;
        y = p.y;
      });
      if (fimX !== null) ctx.fillText(texto, fimX + 6, y);
    });
    ctx.restore();
  },
};

const legenda = (tema) => ({ position: "bottom", labels: { color: tema.texto, boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: "circle", font: { size: 11 } } });

/** Pedidos à IA por dia, empilhados pela função; o total e o custo estimado no tooltip. */
function GraficoPorDia({ porDia, darkMode }) {
  const tema = temaGrafico(darkMode);
  const series = FUNCOES.filter((f) => porDia.some((d) => d[f.codigo] > 0));
  if (!series.length) return <Vazio texto="Nenhum pedido à IA no período." />;
  const data = {
    labels: porDia.map((d) => moment(d.dia).format("DD/MM")),
    datasets: series.map((f) => ({
      label: f.curto,
      data: porDia.map((d) => d[f.codigo] || 0),
      backgroundColor: corDaFuncao(f.codigo, darkMode),
      borderColor: tema.superficie,
      borderWidth: { top: 2 },
      borderSkipped: false,
      borderRadius: { topLeft: 3, topRight: 3 },
      maxBarThickness: 34,
    })),
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: legenda(tema),
      tooltip: {
        callbacks: {
          label: (c) => ` ${c.dataset.label}: ${formatarNumero(c.raw)}`,
          footer: (itens) => {
            const d = porDia[itens[0].dataIndex];
            return `Total: ${formatarNumero(itens.reduce((s, i) => s + (i.raw || 0), 0))} pedido(s) · ${usd(d.custo)} estimado${d.barrados ? ` · ${d.barrados} barrado(s)` : ""}`;
          },
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: tema.textoSecundario, font: { size: 10 } } },
      y: { stacked: true, beginAtZero: true, grid: { color: tema.grade }, border: { display: false }, ticks: { color: tema.textoSecundario, precision: 0, font: { size: 10 } } },
    },
  };
  return <MDBox sx={{ height: 290 }}><Bar data={data} options={options} /></MDBox>;
}

/** Quem mais usou: os 10 com mais pedidos, empilhados pela função, com o custo na ponta. */
function GraficoUsuarios({ usuarios, darkMode, custoDe }) {
  const tema = temaGrafico(darkMode);
  const itens = usuarios.filter((u) => u.chamadas > 0).slice(0, 10);
  if (!itens.length) return <Vazio texto="Nenhum pedido à IA no período." />;
  const series = FUNCOES.filter((f) => itens.some((u) => (u.porFuncao || {})[f.codigo] > 0));
  const data = {
    labels: itens.map((u) => u.nome),
    datasets: series.map((f) => ({
      label: f.curto,
      data: itens.map((u) => (u.porFuncao || {})[f.codigo] || 0),
      backgroundColor: corDaFuncao(f.codigo, darkMode),
      borderColor: tema.superficie,
      borderWidth: { right: 2 },
      borderSkipped: false,
      maxBarThickness: 20,
    })),
  };
  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 120 } },
    plugins: {
      textoNoFimIa: { cor: tema.texto, textos: itens.map((u) => `${formatarNumero(u.chamadas)} · ${usd(custoDe(u))}`) },
      legend: legenda(tema),
      tooltip: { callbacks: { title: (c) => itens[c[0].dataIndex].nome, label: (c) => ` ${c.dataset.label}: ${formatarNumero(c.raw)}` } },
    },
    scales: {
      x: { stacked: true, beginAtZero: true, grid: { color: tema.grade }, border: { display: false }, ticks: { color: tema.textoSecundario, precision: 0, font: { size: 10 } } },
      y: { stacked: true, grid: { display: false }, ticks: { color: tema.texto, font: { size: 11 }, callback: rotuloQueCabe } },
    },
  };
  return (
    <>
      <MDBox sx={{ height: Math.max(170, itens.length * 32 + 70) }}><Bar data={data} options={options} plugins={[textoNoFim]} /></MDBox>
      <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.85, fontSize: "0.68rem" }}>
        Os 10 com mais pedidos à IA. Na ponta: pedidos · custo.
      </MDTypography>
    </>
  );
}

/** Tabela simples (cabeçalho no tom do painel, números à direita). */
function Tabela({ colunas, linhas, cores, vazio, chave, altura = 380 }) {
  const cab = { fontWeight: "bold", fontSize: "0.72rem", backgroundColor: cores.painel, color: cores.texto, whiteSpace: "nowrap", py: 1 };
  const cel = { fontSize: "0.78rem", color: cores.texto, py: 0.75, verticalAlign: "middle" };
  return (
    <TableContainer sx={{ boxShadow: "none", border: `1px solid ${cores.divisoria}`, borderRadius: 1, maxHeight: altura }}>
      <Table size="small" stickyHeader>
        <TableHead sx={{ display: "table-header-group" }}>
          <TableRow>
            {colunas.map((c) => (
              <TableCell key={c.rotulo} align={c.numero ? "right" : "left"} sx={cab}>
                {c.dica ? <Tooltip title={c.dica} placement="top"><span>{c.rotulo}</span></Tooltip> : c.rotulo}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {linhas.length === 0 && (
            <TableRow><TableCell colSpan={colunas.length} align="center" sx={{ ...cel, py: 2 }}>{vazio}</TableCell></TableRow>
          )}
          {linhas.map((l, i) => (
            <TableRow key={l[chave] ?? i} hover>
              {colunas.map((c) => (
                <TableCell key={c.rotulo} align={c.numero ? "right" : "left"} sx={{ ...cel, whiteSpace: c.numero ? "nowrap" : "normal" }}>
                  {c.valor(l, i)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Barra de progresso com o texto (limite do dia, teto do mês): azul, âmbar a partir de 80%, vermelho no limite. */
function Progresso({ valor, total, darkMode, cores, texto }) {
  const p = total > 0 ? Math.min(100, (valor * 100) / total) : 0;
  const modo = darkMode ? "dark" : "light";
  const cor = p >= 100 ? RUIM[modo] : p >= 80 ? ALERTA[modo] : darkMode ? "#7fb2f0" : "#2a78d6";
  return (
    <MDBox>
      <MDBox sx={{ height: 10, borderRadius: 5, background: cores.divisoria, overflow: "hidden" }}>
        <MDBox sx={{ width: `${p}%`, height: "100%", background: cor, borderRadius: 5 }} />
      </MDBox>
      {texto && <MDTypography variant="caption" sx={{ color: cores.textoSecundario, display: "block", mt: 0.25 }}>{texto}</MDTypography>}
    </MDBox>
  );
}

/**
 * Uso da IA (Ferramentas > Uso da IA, c088): quanto o sistema usou a IA no período, quanto custou (o real da OpenAI, pela
 * chave de administrador da Parametrização, e o estimado pelos tokens), quem mais usou e em quê, quanto cada um já usou do
 * limite de hoje, o gasto do mês contra o teto, a conferência por dia com a OpenAI (a chave usada fora do sistema aparece
 * aí) e os últimos pedidos. "Limites e controles" liga/desliga as funções e define os limites (Editar, c088_U).
 * Backend: GET /ia-uso/painel.
 */
export default function UsoIa() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const cores = useMemo(() => coresDoTema(darkMode), [darkMode]);
  const modo = darkMode ? "dark" : "light";

  const [inicio, setInicio] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [fim, setFim] = useState(moment().format("YYYY-MM-DD"));
  const [funcoes, setFuncoes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [controlesAbertos, setControlesAbertos] = useState(false);
  const [busca, setBusca] = useState("");

  const carregar = useCallback((atualizar = false) => {
    if (!inicio || !fim) {
      setErro("Escolha o período.");
      return;
    }
    const q = new URLSearchParams({ inicio, fim });
    funcoes.forEach((f) => q.append("funcoes", f.value));
    usuarios.forEach((u) => q.append("usuarios", u.value));
    if (atualizar) q.append("atualizar", "true");
    setCarregando(true);
    setErro("");
    get(`/ia-uso/painel?${q.toString()}`)
      .then(setDados)
      .catch((e) => setErro(e?.message || "Erro ao carregar o uso da IA."))
      .finally(() => setCarregando(false));
  }, [inicio, fim, funcoes, usuarios]);

  useEffect(() => {
    carregar(false);
  }, [carregar]);

  const t = dados?.totais;
  const openai = useMemo(() => dados?.openai || {}, [dados]);
  const comReal = openai.configurada && !openai.erro;
  const cotacao = dados?.cotacao;
  const mes = dados?.mes;
  const filtrado = funcoes.length > 0 || usuarios.length > 0;
  // Custo de cada um: o real da OpenAI rateado pelo uso (quando a OpenAI dá o custo só do sistema), senão o estimado
  const custoDe = (l) => (l.custoReal !== null && l.custoReal !== undefined ? l.custoReal : l.custo);
  const rateado = t && t.custoReal !== null && t.custoReal !== undefined;
  const usuariosComUso = (dados?.porUsuario || []).filter((u) => u.chamadas > 0 || u.reaproveitados > 0 || u.barrados > 0);
  const controlesPorFuncao = useMemo(() => Object.fromEntries((dados?.controles?.funcoes || []).map((f) => [f.codigo, f])), [dados]);

  const ultimosFiltrados = useMemo(() => {
    const b = busca.trim().toLowerCase();
    const lista = dados?.ultimos || [];
    if (!b) return lista;
    return lista.filter((l) => [l.usuario, l.nomeFuncao, SITUACOES[l.situacao]?.label, l.motivo, l.modelo].some((v) => (v || "").toString().toLowerCase().includes(b)));
  }, [dados, busca]);

  const conferencia = useMemo(() => {
    const custoDia = Object.fromEntries((openai.porDia || []).map((d) => [d.dia, d.custo]));
    return [...(openai.conferencia || [])].reverse().map((c) => ({ ...c, custo: custoDia[c.dia] }));
  }, [openai]);
  // O dia em que o registro começou é parcial (os pedidos de antes da subida não foram registrados): fica fora do aviso
  const diasComDiferenca = conferencia.filter((c) => c.diferenca > 0 && !c.inicioDoRegistro);
  const foraDoSistema = openai.modelosForaDoSistema || [];

  const colunasFuncao = [
    {
      rotulo: "Função",
      valor: (l) => (
        <MDBox display="flex" alignItems="center" gap={0.75} sx={{ color: "inherit" }}>
          <MDBox sx={{ width: 10, height: 10, minWidth: 10, borderRadius: "50%", background: corDaFuncao(l.funcao, darkMode) }} />
          {l.nome}
        </MDBox>
      ),
    },
    {
      rotulo: "Controle",
      valor: (l) => {
        const c = controlesPorFuncao[l.funcao];
        if (!c) return "-";
        return c.ativa
          ? <span>{c.limiteDia ? `${c.limiteDia} por usuário/dia` : "Sem limite"}</span>
          : <Chip size="small" variant="outlined" color="error" label="Desligada" sx={{ height: 20, fontSize: "0.7rem" }} />;
      },
    },
    { rotulo: "Pedidos", numero: true, dica: "Pedidos que foram à IA (atendidos, descartados e com erro)", valor: (l) => formatarNumero(l.chamadas) },
    { rotulo: "Atendidos", numero: true, valor: (l) => formatarNumero(l.atendidos) },
    { rotulo: "Reaproveitados", numero: true, dica: "Resumos já feitos, mostrados sem chamar a IA (sem custo)", valor: (l) => formatarNumero(l.reaproveitados) },
    { rotulo: "Barrados", numero: true, dica: "Função desligada, limite do dia ou teto do mês", valor: (l) => formatarNumero(l.barrados) },
    { rotulo: "Erros", numero: true, valor: (l) => formatarNumero(l.erros + l.descartados) },
    { rotulo: "Tokens", numero: true, dica: "Entrada + saída", valor: (l) => tokens(l.tokensEntrada + l.tokensSaida) },
    { rotulo: rateado ? "Custo (real rateado)" : "Custo estimado", numero: true, valor: (l) => dinheiro(custoDe(l), cotacao) },
  ];

  const colunasUsuario = [
    { rotulo: "#", valor: (l, i) => i + 1 },
    { rotulo: "Usuário", valor: (l) => l.nome },
    { rotulo: "Pedidos", numero: true, valor: (l) => <b>{formatarNumero(l.chamadas)}</b> },
    ...FUNCOES.map((f) => ({ rotulo: f.curto, numero: true, valor: (l) => formatarNumero((l.porFuncao || {})[f.codigo] || 0) })),
    { rotulo: "Reaprov.", numero: true, dica: "Resumos já feitos, sem custo", valor: (l) => formatarNumero(l.reaproveitados) },
    { rotulo: "Barrados", numero: true, valor: (l) => formatarNumero(l.barrados) },
    { rotulo: "Tokens", numero: true, valor: (l) => tokens(l.tokensEntrada + l.tokensSaida) },
    { rotulo: rateado ? "Custo (real rateado)" : "Custo estimado", numero: true, valor: (l) => dinheiro(custoDe(l), cotacao) },
    { rotulo: "Último uso", valor: (l) => l.ultimoUso || "-" },
  ];

  const colunasUltimos = [
    { rotulo: "Quando", valor: (l) => l.data },
    { rotulo: "Usuário", valor: (l) => l.usuario || "-" },
    {
      rotulo: "Função",
      valor: (l) => (
        <MDBox display="flex" alignItems="center" gap={0.75} sx={{ color: "inherit" }}>
          <MDBox sx={{ width: 8, height: 8, minWidth: 8, borderRadius: "50%", background: corDaFuncao(l.funcao, darkMode) }} />
          {nomeCurto(l.funcao)}
        </MDBox>
      ),
    },
    { rotulo: "Situação", valor: (l) => chipSituacao(l.situacao) },
    { rotulo: "Tokens", numero: true, dica: "Entrada / saída", valor: (l) => (l.tokensEntrada || l.tokensSaida ? `${formatarNumero(l.tokensEntrada)} / ${formatarNumero(l.tokensSaida)}` : "-") },
    { rotulo: "Custo est.", numero: true, valor: (l) => (l.custo !== null && l.custo !== undefined ? usd(l.custo) : "-") },
    { rotulo: "Tempo", numero: true, valor: (l) => (l.duracaoMs ? `${(l.duracaoMs / 1000).toFixed(1).replace(".", ",")} s` : "-") },
    { rotulo: "Motivo / modelo", valor: (l) => l.motivo || l.modelo || "-" },
  ];

  const colunasConferencia = [
    { rotulo: "Dia (UTC)", valor: (l) => moment(l.dia).format("DD/MM/YYYY") },
    { rotulo: "Na OpenAI", numero: true, dica: "Pedidos que a OpenAI atendeu pela chave do sistema, nos modelos que o sistema usa", valor: (l) => formatarNumero(l.openai) },
    { rotulo: "No sistema", numero: true, dica: "Pedidos atendidos registrados aqui (atendidos e descartados)", valor: (l) => formatarNumero(l.sistema) },
    {
      rotulo: "Diferença",
      numero: true,
      valor: (l) => {
        if (l.antesDoRegistro) return <Tooltip title="O sistema ainda não registrava os pedidos neste dia"><span style={{ color: cores.textoSecundario }}>sem registro</span></Tooltip>;
        if (l.diferenca > 0 && l.inicioDoRegistro) {
          return <Tooltip title="Dia em que o registro começou: os pedidos de antes da subida não foram registrados"><span>+{formatarNumero(l.diferenca)} (início)</span></Tooltip>;
        }
        return l.diferenca > 0 ? (
          <MDBox component="span" display="inline-flex" alignItems="center" gap={0.25} sx={{ color: TEXTO_ALERTA[modo], fontWeight: 600 }}>
            <Icon sx={{ fontSize: "0.9rem !important", color: "inherit" }}>warning</Icon>+{formatarNumero(l.diferenca)}
          </MDBox>
        ) : formatarNumero(l.diferenca);
      },
    },
    { rotulo: "Outros modelos", numero: true, dica: "Pedidos pela chave do sistema em modelos que o sistema não usa (outro programa com a chave)", valor: (l) => (l.outrosModelos ? formatarNumero(l.outrosModelos) : "-") },
    { rotulo: "Custo real do dia", numero: true, valor: (l) => (l.custo !== undefined ? dinheiro(l.custo, cotacao) : "-") },
  ];

  const colunasModelo = [
    { rotulo: "Modelo", valor: (l) => l.modelo },
    { rotulo: "Pedidos", numero: true, valor: (l) => formatarNumero(l.requisicoes) },
    { rotulo: "Entrada", numero: true, valor: (l) => tokens(l.tokensEntrada) },
    { rotulo: "Em cache", numero: true, valor: (l) => tokens(l.tokensCache) },
    { rotulo: "Saída", numero: true, valor: (l) => tokens(l.tokensSaida) },
  ];

  const escopoCusto = {
    CHAVE: "só da chave do sistema",
    PROJETO: "do projeto inteiro da chave do sistema (a OpenAI não separou por chave)",
    ORGANIZACAO: "da organização inteira (a chave do sistema ainda não foi identificada: Parametrização do Sistema > OpenAI > Testar)",
  };

  const cardCusto = comReal ? (
    <KpiCard compacto icone="paid" cor={NEUTRO[modo]} rotulo="Custo real (OpenAI)" valor={usd(openai.custoReal)}
      detalhe={`${brl(openai.custoReal, cotacao) ? `${brl(openai.custoReal, cotacao)} · ` : ""}estimado ${usd(t.custo)}`} detalheFixo />
  ) : (
    <KpiCard compacto icone="paid" cor={NEUTRO[modo]} rotulo="Custo estimado" valor={usd(t?.custo)}
      detalhe={brl(t?.custo, cotacao) || (openai.erro ? "OpenAI fora: só o estimado" : "sem a chave de administrador")} detalheFixo />
  );

  return (
    <Grid container spacing={3} pt={1} pb={3}>
      <Grid item xs={12}>
        <Card sx={{ px: 2, py: 1.5, background: cores.cartao }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={6} xl={3}>
              <MDPeriodo inicio={inicio} fim={fim} onChange={({ inicio: i, fim: f }) => { setInicio(i); setFim(f); }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3} xl={3}>
              <InputLabel sx={rotuloFiltro}>Função</InputLabel>
              <MDSelect isMulti name="funcoes" placeholder="Todas" value={funcoes} options={OPCOES_FUNCAO} onChange={(v) => setFuncoes(v || [])} />
            </Grid>
            <Grid item xs={12} sm={6} md={3} xl={3}>
              <InputLabel sx={rotuloFiltro}>Usuário</InputLabel>
              <MDSelect isMulti name="usuarios" placeholder="Todos" value={usuarios} options={dados?.opcoes?.usuarios || []} onChange={(v) => setUsuarios(v || [])} />
            </Grid>
            <Grid item xs={12} sm={6} md={6} xl={1.5}>
              <InputLabel sx={rotuloFiltro}>&nbsp;</InputLabel>
              <MDButton variant="contained" color="info" size="medium" fullWidth onClick={() => carregar(true)} disabled={carregando} sx={{ height: 38, px: 1, whiteSpace: "nowrap" }}>
                {carregando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.5 }} /> : <Icon sx={{ mr: 0.5 }}>refresh</Icon>}
                Atualizar
              </MDButton>
            </Grid>
            <Grid item xs={12} sm={6} md={6} xl={1.5}>
              <InputLabel sx={rotuloFiltro}>&nbsp;</InputLabel>
              <MDButton variant="outlined" color="info" size="medium" fullWidth onClick={() => setControlesAbertos(true)} sx={{ height: 38, px: 1, whiteSpace: "nowrap" }}>
                <Icon sx={{ mr: 0.5 }}>tune</Icon>
                Limites
              </MDButton>
            </Grid>
            <Grid item xs={12} sx={{ pt: "8px !important" }}>
              {erro && <MDTypography variant="caption" color="error" fontWeight="bold" sx={{ px: 1, display: "block" }}>{erro}</MDTypography>}
              <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                Cada pedido do sistema à IA fica registrado (quem, função, tokens, custo e tempo; nunca o texto). O custo real vem da OpenAI pela
                chave de administrador (Parametrização do Sistema &gt; OpenAI); a OpenAI não sabe quem pediu, então quem e em quê vêm do registro do
                sistema. Função e usuário filtram só o registro. Os pedidos de antes de 23/10/2026 não foram registrados.
                {dados?.geradoEm ? ` Dados de ${dados.geradoEm}${openai.lidoEm ? ` (OpenAI lida às ${openai.lidoEm.substring(11)})` : ""}.` : ""}
              </MDTypography>
            </Grid>
          </Grid>
        </Card>
      </Grid>

      {carregando && dados && (
        <Grid item xs={12} sx={{ pt: "12px !important" }}><LinearProgress color="info" sx={{ borderRadius: 1 }} /></Grid>
      )}
      {carregando && !dados && (
        <Grid item xs={12}><MDBox display="flex" justifyContent="center" py={8}><CircularProgress size={36} color="info" /></MDBox></Grid>
      )}

      {dados && t && (
        <Grid item xs={12} sx={{ opacity: carregando ? 0.5 : 1, transition: "opacity 0.2s" }}>
          <Grid container spacing={3}>
            {mes?.bloqueado && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ fontSize: "0.85rem" }}>
                  O teto de gasto do mês ({usd(mes.teto)}) foi atingido: nenhuma função usa a IA até o dia 1º ou até aumentar o teto em Limites.
                </Alert>
              </Grid>
            )}
            {!openai.configurada && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ fontSize: "0.85rem" }}>{openai.motivo}</Alert>
              </Grid>
            )}
            {openai.erro && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ fontSize: "0.85rem" }}>Dados reais da OpenAI indisponíveis: {openai.erro} Os números abaixo são do registro do sistema.</Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={6} lg={4} xl={2}>{cardCusto}</Grid>
            <Grid item xs={12} sm={6} lg={4} xl={2}>
              <KpiCard compacto icone="psychology" cor={NEUTRO[modo]} rotulo="Pedidos à IA" valor={formatarNumero(t.chamadas)}
                detalhe={`${formatarNumero(t.atendidos)} atendido(s)${comReal && !filtrado ? ` · OpenAI: ${formatarNumero(openai.requisicoesSistema ?? openai.requisicoes)}` : ""}`} detalheFixo />
            </Grid>
            <Grid item xs={12} sm={6} lg={4} xl={2}>
              <KpiCard compacto icone="data_usage" cor={NEUTRO[modo]} rotulo="Tokens" valor={tokens(t.tokensEntrada + t.tokensSaida)}
                detalhe={`entrada ${tokens(t.tokensEntrada)} · saída ${tokens(t.tokensSaida)}`} detalheFixo />
            </Grid>
            <Grid item xs={12} sm={6} lg={4} xl={2}>
              <KpiCard compacto icone="group" cor={NEUTRO[modo]} rotulo="Usuários" valor={formatarNumero(usuariosComUso.length)}
                detalhe={usuariosComUso[0] && usuariosComUso[0].chamadas > 0 ? `quem mais usou: ${usuariosComUso[0].nome}` : "ninguém usou no período"} detalheFixo />
            </Grid>
            <Grid item xs={12} sm={6} lg={4} xl={2}>
              <KpiCard compacto icone="recycling" cor={BOM[modo]} rotulo="Reaproveitados" valor={formatarNumero(t.reaproveitados)}
                detalhe="resumos já feitos, sem custo" detalheFixo />
            </Grid>
            <Grid item xs={12} sm={6} lg={4} xl={2}>
              <KpiCard compacto icone="block" cor={RUIM[modo]} rotulo="Barrados e erros" valor={formatarNumero(t.barrados + t.erros + t.descartados)}
                detalhe={`${formatarNumero(t.barrados)} barrado(s) · ${formatarNumero(t.erros + t.descartados)} erro(s)`} detalheFixo />
            </Grid>

            <Grid item xs={12} xl={8}>
              <Painel rotulo={<Titulo icone="bar_chart" texto="Pedidos à IA por dia" />}>
                <GraficoPorDia porDia={dados.porDia || []} darkMode={darkMode} />
              </Painel>
            </Grid>
            <Grid item xs={12} xl={4}>
              <Painel rotulo={<Titulo icone="savings" texto={`Gasto do mês (${moment(`${mes.mes}-01`).format("MM/YYYY")})`} />}>
                <MDBox display="flex" alignItems="baseline" gap={1} mb={1}>
                  <MDTypography variant="h4" sx={{ color: cores.texto }}>{usd(mes.usado)}</MDTypography>
                  {mes.teto && <MDTypography variant="button" sx={{ color: cores.textoSecundario }}>de {usd(mes.teto)}</MDTypography>}
                </MDBox>
                {mes.teto ? (
                  <Progresso valor={Number(mes.usado)} total={Number(mes.teto)} darkMode={darkMode} cores={cores}
                    texto={`${String(mes.percentual).replace(".", ",")}% do teto${brl(mes.usado, cotacao) ? ` · ${brl(mes.usado, cotacao)}` : ""}`} />
                ) : (
                  <MDTypography variant="caption" sx={{ color: cores.textoSecundario, display: "block" }}>Sem teto de gasto definido (Limites).</MDTypography>
                )}
                <MDBox mt={2} display="flex" flexDirection="column" gap={0.5}>
                  <MDTypography variant="caption" sx={{ color: cores.texto }}>
                    Real da OpenAI: <b>{mes.real !== null && mes.real !== undefined ? usd(mes.real) : "-"}</b>
                    {mes.realLidoEm ? ` (lido em ${mes.realLidoEm})` : openai.configurada ? " (lendo...)" : " (sem a chave de administrador)"}
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ color: cores.texto }}>Estimado pelo sistema: <b>{usd(mes.estimado)}</b></MDTypography>
                  <MDTypography variant="caption" sx={{ color: cores.textoSecundario, fontSize: "0.68rem" }}>
                    Vale o maior dos dois: a OpenAI fecha o custo por dia (UTC) e a estimativa cobre as horas que ainda não apareceram lá.
                  </MDTypography>
                </MDBox>
              </Painel>
            </Grid>

            <Grid item xs={12} xl={7}>
              <Painel rotulo={<Titulo icone="leaderboard" texto="Quem mais usou" />}>
                <GraficoUsuarios usuarios={dados.porUsuario || []} darkMode={darkMode} custoDe={custoDe} />
              </Painel>
            </Grid>
            <Grid item xs={12} xl={5}>
              <Painel rotulo={<Titulo icone="today" texto="Limite usado hoje" />}>
                {(dados.hoje || []).length === 0 ? (
                  <Vazio texto="Ninguém usou hoje uma função com limite." />
                ) : (
                  <MDBox display="flex" flexDirection="column" gap={1.25} sx={{ maxHeight: 360, overflowY: "auto", pr: 1 }}>
                    {dados.hoje.map((h) => (
                      <MDBox key={`${h.usuario}-${h.funcao}`}>
                        <MDBox display="flex" justifyContent="space-between" gap={1}>
                          <MDTypography variant="caption" sx={{ color: cores.texto }}>
                            <b>{h.usuario}</b> · {nomeCurto(h.funcao)}
                          </MDTypography>
                          <MDTypography variant="caption" sx={{ color: cores.texto, whiteSpace: "nowrap" }}>
                            {h.usadas} de {h.limite}{h.barrados ? ` · ${h.barrados} barrado(s)` : ""}
                          </MDTypography>
                        </MDBox>
                        <Progresso valor={h.usadas} total={h.limite} darkMode={darkMode} cores={cores} />
                      </MDBox>
                    ))}
                  </MDBox>
                )}
              </Painel>
            </Grid>

            <Grid item xs={12}>
              <Painel rotulo={<Titulo icone="category" texto="Por função" />}>
                <Tabela colunas={colunasFuncao} linhas={dados.porFuncao || []} cores={cores} chave="funcao" vazio="Nenhuma função." />
              </Painel>
            </Grid>

            <Grid item xs={12}>
              <Painel rotulo={<Titulo icone="people" texto="Por usuário" />}>
                <Tabela colunas={colunasUsuario} linhas={usuariosComUso} cores={cores} chave="nome" vazio="Ninguém usou a IA no período." />
                {rateado && (
                  <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.85, fontSize: "0.68rem" }}>
                    Custo real rateado: o custo que a OpenAI cobrou da chave do sistema, dividido pelo uso de cada um (tokens × preço).
                  </MDTypography>
                )}
              </Painel>
            </Grid>

            {comReal && (
              <Grid item xs={12} xl={7}>
                <Painel rotulo={<Titulo icone="fact_check" texto="Conferência com a OpenAI (por dia)" />}>
                  <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1 }}>
                    Chave do sistema: <b>{trechoDaChave(openai.chaveDescricao) || "não identificada"}</b>. Custo {escopoCusto[openai.escopoCusto] || ""}.
                    {" "}Os dias da OpenAI são em UTC (fecham às 21h de Brasília); o sistema é contado no mesmo horário. Antes de o sistema
                    registrar os pedidos não há o que comparar.
                  </MDTypography>
                  {diasComDiferenca.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 1, fontSize: "0.8rem" }}>
                      Em {diasComDiferenca.length} dia(s) a OpenAI atendeu mais pedidos pela chave do sistema do que o sistema registrou: a chave pode estar
                      sendo usada fora do sistema. Troque a chave na OpenAI e grave a nova na Parametrização.
                    </Alert>
                  )}
                  {foraDoSistema.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 1, fontSize: "0.8rem" }}>
                      A chave do sistema também foi usada em modelos que o sistema não usa:{" "}
                      <b>{foraDoSistema.map((m) => `${m.modelo} (${formatarNumero(m.requisicoes)} pedido(s))`).join(", ")}</b>. Outro programa está
                      com a chave do sistema (ex.: a narração dos vídeos tutoriais). O custo real inclui esse uso, então o custo de cada usuário fica
                      só o estimado. Para separar, crie na OpenAI uma chave própria para esse programa.
                    </Alert>
                  )}
                  {(openai.outrasChaves || []).length > 0 && (
                    <Alert severity="info" sx={{ mb: 1, fontSize: "0.8rem" }}>
                      Outras chaves do mesmo projeto usaram a OpenAI no período: {openai.outrasChaves.map((k) => `${k.nome} (${formatarNumero(k.requisicoes)})`).join(", ")}.
                    </Alert>
                  )}
                  <Tabela colunas={colunasConferencia} linhas={conferencia} cores={cores} chave="dia" altura={340} vazio="Nenhum pedido no período." />
                </Painel>
              </Grid>
            )}
            {comReal && (
              <Grid item xs={12} xl={5}>
                <Painel rotulo={<Titulo icone="memory" texto="Consumo na OpenAI por modelo" />}>
                  <Tabela colunas={colunasModelo} linhas={openai.porModelo || []} cores={cores} chave="modelo" altura={340} vazio="Nenhum pedido no período." />
                  <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.85, fontSize: "0.68rem" }}>
                    Total da chave do sistema: {formatarNumero(openai.requisicoes)} pedido(s), {tokens(openai.tokensEntrada)} tokens de entrada
                    ({tokens(openai.tokensCache)} em cache) e {tokens(openai.tokensSaida)} de saída. Custo real {dinheiro(openai.custoReal, cotacao)}.
                    {foraDoSistema.length > 0 ? ` Modelos que o sistema não usa: ${foraDoSistema.map((m) => m.modelo).join(", ")}.` : ""}
                  </MDTypography>
                </Painel>
              </Grid>
            )}

            <Grid item xs={12}>
              <Painel
                rotulo={<Titulo icone="history" texto="Últimos pedidos" />}
                acao={(
                  <MDBox sx={{ width: 280 }}>
                    <MDInput size="small" fullWidth placeholder="Buscar usuário, função, situação..." value={busca} onChange={(e) => setBusca(e.target.value)} />
                  </MDBox>
                )}
              >
                <Tabela colunas={colunasUltimos} linhas={ultimosFiltrados} cores={cores} chave="id" vazio="Nenhum pedido no período." />
                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.85, fontSize: "0.68rem" }}>
                  Os {formatarNumero((dados.ultimos || []).length)} mais recentes do período (até 300).
                </MDTypography>
              </Painel>
            </Grid>
          </Grid>
        </Grid>
      )}

      <ControlesIa open={controlesAbertos} onClose={() => setControlesAbertos(false)} onSalvo={() => carregar(false)} />
    </Grid>
  );
}
